"use client";

import {
  BarChart3,
  Languages,
  Palette,
  TrendingUp,
  Upload,
  Video,
} from "lucide-react";
import { useJsonTranslation } from "@/hooks/useJsonTranslation";

const features = [
  {
    icon: Video,
    titleKey: "features.deepVideoAnalysis.title",
    descriptionKey: "features.deepVideoAnalysis.description",
    stats: [
      {
        labelKey: "features.deepVideoAnalysis.stats.context.label",
        valueKey: "features.deepVideoAnalysis.stats.context.value",
      },
      {
        labelKey: "features.deepVideoAnalysis.stats.speech.label",
        valueKey: "features.deepVideoAnalysis.stats.speech.value",
      },
      {
        labelKey: "features.deepVideoAnalysis.stats.meaning.label",
        valueKey: "features.deepVideoAnalysis.stats.meaning.value",
      },
    ],
  },
  {
    icon: Palette,
    titleKey: "features.aiThumbnails.title",
    descriptionKey: "features.aiThumbnails.description",
    stats: [
      {
        labelKey: "features.aiThumbnails.stats.source.label",
        valueKey: "features.aiThumbnails.stats.source.value",
      },
      {
        labelKey: "features.aiThumbnails.stats.options.label",
        valueKey: "features.aiThumbnails.stats.options.value",
      },
      {
        labelKey: "features.aiThumbnails.stats.text.label",
        valueKey: "features.aiThumbnails.stats.text.value",
      },
    ],
  },
  {
    icon: TrendingUp,
    titleKey: "features.multiPlatformSeo.title",
    descriptionKey: "features.multiPlatformSeo.description",
    stats: [
      {
        labelKey: "features.multiPlatformSeo.stats.platforms.label",
        valueKey: "features.multiPlatformSeo.stats.platforms.value",
      },
      {
        labelKey: "features.multiPlatformSeo.stats.angles.label",
        valueKey: "features.multiPlatformSeo.stats.angles.value",
      },
      {
        labelKey: "features.multiPlatformSeo.stats.intent.label",
        valueKey: "features.multiPlatformSeo.stats.intent.value",
      },
    ],
  },
  {
    icon: Languages,
    titleKey: "features.languages.title",
    descriptionKey: "features.languages.description",
    stats: [
      {
        labelKey: "features.languages.stats.languages.label",
        valueKey: "features.languages.stats.languages.value",
      },
      {
        labelKey: "features.languages.stats.dubbing.label",
        valueKey: "features.languages.stats.dubbing.value",
      },
      {
        labelKey: "features.languages.stats.voiceClone.label",
        valueKey: "features.languages.stats.voiceClone.value",
      },
    ],
  },
  {
    icon: Upload,
    titleKey: "features.oneFlow.title",
    descriptionKey: "features.oneFlow.description",
    stats: [
      {
        labelKey: "features.oneFlow.stats.upload.label",
        valueKey: "features.oneFlow.stats.upload.value",
      },
      {
        labelKey: "features.oneFlow.stats.review.label",
        valueKey: "features.oneFlow.stats.review.value",
      },
      {
        labelKey: "features.oneFlow.stats.control.label",
        valueKey: "features.oneFlow.stats.control.value",
      },
    ],
  },
  {
    icon: BarChart3,
    titleKey: "features.deepIntelligence.title",
    descriptionKey: "features.deepIntelligence.description",
    stats: [
      {
        labelKey: "features.deepIntelligence.stats.before.label",
        valueKey: "features.deepIntelligence.stats.before.value",
      },
      {
        labelKey: "features.deepIntelligence.stats.after.label",
        valueKey: "features.deepIntelligence.stats.after.value",
      },
      {
        labelKey: "features.deepIntelligence.stats.why.label",
        valueKey: "features.deepIntelligence.stats.why.value",
      },
    ],
  },
];

export function Features() {
  const { t } = useJsonTranslation("HomeFeatures");

  return (
    <div
      data-home-block="feature-highlights"
      className="relative overflow-hidden px-5 py-6 sm:px-7 sm:py-8"
    >
      <div className="relative z-10">
        {/* Section Header */}
        <div className="mb-10 text-center">
          <div className="mb-6 flex flex-wrap items-center justify-center gap-3">
            <div className="home-badge">
              <span>{t("header.badge")}</span>
            </div>
            <div className="home-badge border-leaf-500/25 bg-leaf-500/10">
              <span className="text-leaf-600 dark:text-leaf-400">
                {t("header.patent")}
              </span>
            </div>
          </div>

          <h2 className="home-section-title mb-6 text-3xl text-balance md:text-4xl lg:text-5xl">
            {t("header.title")}
          </h2>

          <p className="home-section-copy mx-auto max-w-3xl text-lg text-pretty md:text-xl">
            {t("header.subtitle")}
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {features.map((feature, i) => (
            <div key={i} className="uni-card group relative overflow-hidden">
              <div className="relative flex items-start gap-4">
                <div className="home-icon-tile h-12 w-12 shrink-0 transition-transform duration-300 group-hover:scale-105">
                  <feature.icon className="h-6 w-6" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-display text-foreground mb-2 text-lg font-semibold">
                    {t(feature.titleKey)}
                  </h3>
                  <p className="home-section-copy mb-4 text-sm">
                    {t(feature.descriptionKey)}
                  </p>
                  <div className="flex flex-wrap gap-3 sm:gap-4">
                    {feature.stats.map((stat, j) => (
                      <div key={j} className="text-center">
                        <div className="text-foreground text-sm font-bold tabular-nums">
                          {t(stat.valueKey)}
                        </div>
                        <div className="text-muted-foreground/60 text-xs">
                          {t(stat.labelKey)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Feature Pills */}
        <div className="mt-12 flex flex-wrap items-center justify-center gap-2.5">
          {[
            "pills.oneClickOptimization",
            "pills.fiveModels",
            "pills.successFromFirstSecond",
            "pills.basedOnYourContent",
            "pills.fullChannelAnalysis",
            "pills.prioritySupport",
          ].map((pill, i) => (
            <div
              key={i}
              className="home-chip transition-all duration-300 hover:-translate-y-0.5"
            >
              {t(pill)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
